import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SearchX } from 'lucide-react';
import { EmptyState } from './EmptyState.js';

export interface ActiveFilter {
  key: string;
  label: string;
}

function DebouncedSearch({ value, onChange, placeholder }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const [text, setText] = useState(value);
  const lastEmitted = useRef(value);

  // External changes (chip removal, clear all) override the local text.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    if (text === lastEmitted.current) return;
    const id = setTimeout(() => {
      lastEmitted.current = text;
      onChange(text);
    }, 300);
    return () => clearTimeout(id);
  }, [text, onChange]);

  return (
    <div className="relative flex-1 min-w-[180px]">
      <input
        type="search"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-bg-panel border border-border rounded-lg px-3 py-2 pr-8 text-sm text-white placeholder:text-muted focus:outline-none focus:border-accent"
      />
      {text && (
        <button
          type="button"
          onClick={() => { lastEmitted.current = ''; setText(''); onChange(''); }}
          aria-label="Clear search"
          className="absolute right-0 top-1/2 -translate-y-1/2 h-9 w-9 text-muted hover:text-white text-sm"
        >
          ✕
        </button>
      )}
    </div>
  );
}

/**
 * Unified filter toolbar: debounced search + page-supplied dropdown controls,
 * with active-filter chips and a "Clear all" action below. Changing any filter
 * is expected to reset pagination via the page's useTableControls filterDeps.
 * Pages may persist non-search filters with useUserPreference.
 */
export function FilterBar({ search, onSearchChange, searchPlaceholder = 'Search…', activeFilters, onRemoveFilter, onClearAll, children }: {
  search?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  activeFilters: ActiveFilter[];
  onRemoveFilter: (key: string) => void;
  onClearAll: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row flex-wrap gap-2">
        {search !== undefined && onSearchChange && (
          <DebouncedSearch value={search} onChange={onSearchChange} placeholder={searchPlaceholder} />
        )}
        {children}
      </div>
      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {activeFilters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => onRemoveFilter(f.key)}
              className="inline-flex min-h-9 items-center gap-1 px-2 py-1 rounded-md border border-border bg-bg-card text-xs text-white hover:border-accent"
            >
              {f.label} <span aria-hidden className="text-muted">✕</span>
            </button>
          ))}
          <button
            type="button"
            onClick={onClearAll}
            className="ml-1 min-h-9 px-2 text-xs text-muted hover:text-white underline underline-offset-2"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

/** Empty-state row for filtered tables that returned zero rows. */
export function EmptyFilterRow({ colSpan, onClearAll }: { colSpan: number; onClearAll: () => void }) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <EmptyState
          icon={SearchX}
          title="No results match your filters."
          action={{ label: 'Clear all', onClick: onClearAll }}
        />
      </td>
    </tr>
  );
}
